/*
  What this reader is NOT seeing, said where the numbers are.

  EVERY ROLL-UP IN THIS PRODUCT IS A PURE FUNCTION OF `state.employees`. That is the design — the
  server sends a whole `SeparationState` and `@integrate/core` recomputes in the browser — and it is
  what makes a scope cheap: filter the array and every total is the slice's, correct for that
  population, with no second aggregated payload for the client to disagree with.

  It is also what makes this component necessary. A scoped member's stranded cost is right about their
  slice and is NOT the company's stranded cost, and the two are indistinguishable on screen. A number
  that is right about a part and read as being about the whole is a wrong answer in a steering
  committee, and it will be screenshotted into one.

  STRUCTURAL, NOT DECORATIVE. It renders from `state.scope`, which the server sets, so it cannot be
  lost by a pane that forgot it — and it is deliberately not dismissible, because the thing it is
  telling you does not stop being true when you close it.

  IT READS THE STORE ITSELF, which nothing else in `ui/` does, and the exception is the point. The
  alternative is a `notice` prop threaded through four `Workbench` mounts in three files, which is
  four places for the fifth one to forget — and the thing being forgotten is the sentence that stops a
  slice total being read as a company total. A safety notice that a call site can omit is a safety
  notice that will be omitted. So `Workbench` renders this unconditionally and it answers null when
  there is nothing to say, which is every membership until an owner grants otherwise.

  IT NAMES THE FILTER AND NEVER A DENOMINATOR. "412 of 5,180" would put the size of the population
  somebody cannot see into the label meant to protect that population — recoverable by subtraction
  from any total beside it. `describeScope` in core is the one wording.
*/
import { describeScope } from '@integrate/core';
import { useStore } from '../store.js';

export function ScopeNotice({
  where,
}: {
  /** Distinguishes the copies so a test can address the one it means. */
  where: 'chart' | 'workbench';
}): JSX.Element | null {
  const scope = useStore((s) => s.design?.scope);
  if (!scope) return null;

  const named = describeScope(scope.filter);
  if (named === null && !scope.salaryWithheld) return null;

  return (
    <span className="pill warn" data-testid={`scope-notice-${where}`}>
      {named !== null && (
        <>
          {/* The population first: it changes what every number means, where the salary axis changes
              which numbers exist at all. */}
          Showing <strong>{named}</strong> — totals are for this part of the organisation
        </>
      )}
      {named !== null && scope.salaryWithheld && ', and '}
      {scope.salaryWithheld && (named === null ? 'Compensation is not shown' : 'compensation is not shown')}
    </span>
  );
}
